import type { ExtractedField, ExtractedInvoice } from '@dcc-erp/shared';

import type { FieldLoc } from './viewer-types';

const EMPTY: FieldLoc = { page: null, snippet: null, bbox: null };

// Map a backend validation check `code` (see apps/api/.../invoice-validation.ts)
// to the form field-key a "click the message → focus + highlight" should target.
export const VALIDATION_FIELD_MAP: Record<string, string> = {
  seller_tin_format: 'seller.taxId',
  is_full_tax_invoice: 'docTitle',
  buyer_taxid_match: 'buyer.taxId',
  buyer_name_match: 'buyer.name',
  seller_branch: 'seller.branch',
  footing_lines: 'total',
  footing_total: 'total',
  vat_rate: 'vatAmount',
  bevdate_converted: 'invoiceDate',
};

function locFromField(f: ExtractedField | undefined): FieldLoc {
  if (!f) return EMPTY;
  return { page: f.page, snippet: f.sourceSnippet, bbox: null };
}

// Resolve a field-key to its source location in the immutable extraction.
// Line fields (line.<i>.description / line.<i>.amount) carry no per-field
// page/snippet, so they degrade to page:null (no PDF jump) with the description
// text as a best-effort snippet caption; never throws on an unknown key.
export function fieldLocFromExtracted(extracted: ExtractedInvoice, fieldKey: string): FieldLoc {
  const parts = fieldKey.split('.');
  const head = parts[0];

  if (head === 'line') {
    const idx = Number(parts[1]);
    const sub = parts[2];
    const line = Number.isInteger(idx) ? extracted.lineItems[idx] : undefined;
    if (!line) return EMPTY;
    if (sub === 'description') {
      return { page: null, snippet: line.description ?? null, bbox: null };
    }
    return EMPTY; // line.<i>.amount has no snippet/page
  }

  if (parts.length === 1 && head !== undefined) {
    const f = (extracted as Record<string, unknown>)[head] as ExtractedField | undefined;
    // Only ExtractedField-shaped top-level keys are valid targets.
    if (f && typeof f === 'object' && 'page' in f && 'sourceSnippet' in f) {
      return locFromField(f);
    }
    return EMPTY;
  }

  if (parts.length === 2 && (head === 'seller' || head === 'buyer')) {
    const party = extracted[head] as Record<string, ExtractedField>;
    const sub = parts[1];
    return locFromField(sub !== undefined ? party?.[sub] : undefined);
  }

  return EMPTY;
}
